import { Site, SiteAggregateData } from '@domain/graphql.types';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useCallback, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DataSourceControl from '../components/DataSourceControl';
import SiteLookupContext from '../context/SiteLookupContext';
import SiteLookupMapContext from '../context/SiteLookupMapContext';
import { siteLookupTokens } from '../siteLookupTokens';
import ListCard from './ListCard';
import OverviewCard from './OverviewCard';
import StationDetail from './StationDetail';
import { getScrollParent } from './scrollParent';

const { color, size } = siteLookupTokens;

type Props = {
  hasSearchResult: boolean;
  fetching: boolean;
  /** the matching stations, sorted by distance */
  sites: readonly Site[];
  aggregateData?: SiteAggregateData | null;
};

/** Index of the next station, wrapping around the list in both directions. */
export const wrapIndex = (index: number, count: number) => (count ? (index + count) % count : 0);

/**
 * Left panel: the station list, or the sheet of the open station in its place. Back returns to the list
 * where it was scrolled and gives the focus back to the station's row.
 */
const SiteLookupList = ({ hasSearchResult, fetching, sites, aggregateData }: Props) => {
  const { t } = useTranslation();
  const { result } = useContext(SiteLookupContext);
  const { selectedSite, setHoveredSiteId, setSelectedSite } = useContext(SiteLookupMapContext);

  const rootRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef(new Map<string, HTMLButtonElement>());
  const listScrollTop = useRef(0);
  const focusRowOnBack = useRef<string | null>(null);

  const openIndex = selectedSite ? sites.findIndex(({ id }) => id === selectedSite.id) : -1;
  const openSite = openIndex >= 0 ? sites[openIndex] : null;
  const isDetailOpen = !!openSite;
  const isDetailOpenRef = useRef(isDetailOpen);
  isDetailOpenRef.current = isDetailOpen;

  // Remember where the list is scrolled while it is shown, to come back to it from the sheet.
  useEffect(() => {
    if (isDetailOpen) return;

    const onScroll = () => {
      // The page shrinking when the sheet replaces the list must not overwrite the list position.
      if (isDetailOpenRef.current) return;
      listScrollTop.current = getScrollParent(rootRef.current).scrollTop;
    };
    onScroll();
    // Capture: scroll events do not bubble, and the scrolling element may be the page or a layout container.
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true });
  }, [isDetailOpen]);

  // Opening a station shows its sheet from the top and moves the focus into it (previous / next keep the focus
  // on their button); back restores the list.
  const openSiteId = openSite?.id ?? null;
  const previousOpenSiteId = useRef<string | null>(null);
  useLayoutEffect(() => {
    const wasDetailOpen = !!previousOpenSiteId.current;
    previousOpenSiteId.current = openSiteId;
    const scrollParent = getScrollParent(rootRef.current);

    if (openSiteId) {
      setHoveredSiteId(null);
      scrollParent.scrollTop = 0;
      if (!wasDetailOpen) detailRef.current?.focus({ preventScroll: true });
      return;
    }

    if (!wasDetailOpen) return;
    scrollParent.scrollTop = listScrollTop.current;
    if (focusRowOnBack.current) {
      rowRefs.current.get(focusRowOnBack.current)?.focus({ preventScroll: true });
      focusRowOnBack.current = null;
    }
  }, [openSiteId, setHoveredSiteId]);

  const openStation = useCallback((id: string) => setSelectedSite({ id, origin: 'list' }), [setSelectedSite]);

  const goBack = () => {
    focusRowOnBack.current = openSiteId;
    setSelectedSite(null);
  };

  const goTo = (offset: number) => openStation(sites[wrapIndex(openIndex + offset, sites.length)].id);

  const setRowRef = (id: string) => (node: HTMLButtonElement | null) => {
    if (node) rowRefs.current.set(id, node);
    else rowRefs.current.delete(id);
  };

  return (
    <Stack
      ref={rootRef}
      sx={{
        width: '100%',
        minHeight: 'calc(100vh - (var(--top-bar-height) + var(--site-lookup-header-height)))',
        paddingTop: 1.5,
        paddingBottom: 6,
        backgroundColor: '#fff',
      }}
    >
      {openSite ? (
        <StationDetail
          ref={detailRef}
          site={openSite}
          index={openIndex}
          count={sites.length}
          onBack={goBack}
          onPrevious={() => goTo(-1)}
          onNext={() => goTo(1)}
        />
      ) : (
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} minHeight={size.sourceControlHeight} paddingX={1.5}>
            {/* Counts over the whole radius, before the report cap. <output> is a polite live region: they are announced after each search. */}
            <Box component="output" aria-live="polite" display="block">
              {hasSearchResult && !fetching && result && (
                <Typography component="span" fontSize={16} fontWeight={600} color={color.text.primary}>
                  {t('siteLookup.list.stations', { count: result.data.stationsCount })} ·{' '}
                  {t('siteLookup.list.ports', { count: result.data.matchingPortsCount })}
                </Typography>
              )}
            </Box>
            {hasSearchResult && <DataSourceControl />}
          </Box>

          {hasSearchResult && !fetching && (
            <Stack gap={0.5} marginTop={1.5}>
              <Box paddingX={0.5} marginBottom={1}>
                <OverviewCard sites={sites} aggregateData={aggregateData} />
              </Box>

              {!sites.length && (
                <Box paddingX={1.5} paddingY={4} textAlign="center">
                  <Typography fontSize={16} fontWeight={700}>
                    {t('siteLookup.list.empty.title')}
                  </Typography>
                  <Typography color="text.secondary" fontSize={14} marginTop={0.5}>
                    {t('siteLookup.list.empty.hint')}
                  </Typography>
                </Box>
              )}

              {sites.length > 0 && (
                <Box component="ul" aria-label={t('siteLookup.list.label')} sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {sites.map((site) => (
                    <Box component="li" key={site.id} sx={{ borderBottom: `1px solid ${color.border}`, '&:last-of-type': { borderBottom: 0 } }}>
                      <ListCard ref={setRowRef(site.id)} site={site} onOpen={openStation} />
                    </Box>
                  ))}
                </Box>
              )}

              {sites.length > 0 && (
                <Typography fontSize={14} fontWeight={500} paddingX={1.5} marginBlockStart={5} textAlign="center">
                  {t('siteLookup.list.endReached')}
                </Typography>
              )}
            </Stack>
          )}

          {fetching && <CircularProgress sx={{ alignSelf: 'center', marginTop: 32 }} aria-label={t('siteLookup.detail.loading')} />}
        </>
      )}
    </Stack>
  );
};

export default SiteLookupList;
